import { getSeries } from "@stss/hooks/src/useLocalPagination";
import type { Pagination } from "@stss/types";
import classNames from "classnames";
import React from "react";

import { Select } from "@calcom/ui";

const perPageOptions = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "15", value: 15 },
  { label: "25", value: 25 },
  // { label: "50", value: 50 },
];

type TableCol = {
  id: string | number;
  children: string | React.ReactNode;
};

type TableRow = {
  id: string | number;
  cells: RowCell[];
  onClick?: () => void;
};

type RowCell = {
  id: string | number;
  children: React.ReactNode;
};

export type TableProps = {
  cols: TableCol[];
  rows: TableRow[];
  isLoading: boolean;
  paginationProps?: PaginationComponentProps;
  defaultPerPage?: number;
  rowNumber?: boolean;
};

export const Table = ({
  cols,
  rows,
  isLoading,
  paginationProps,
  defaultPerPage = 10,
  rowNumber = false,
}: TableProps) => {
  const perPage = paginationProps?.pagination.perPage || defaultPerPage;
  const page = paginationProps?.pagination.currentPage || 1;

  // calculate the row number considering the current page and per page
  const getRowNumber = (index: number) => {
    return (page - 1) * perPage + index + 1;
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full py-2 ">
          <div className="border-subtle overflow-hidden rounded-lg border">
            <table className="divide-subtle h-full min-w-full divide-y">
              <thead className="dark:bg-muted bg-gray-50">
                <tr>
                  {rowNumber && (
                    <th scope="col" className="py-3.5 text-left text-sm font-normal rtl:text-right">
                      <div className={classNames("flex justify-end")}>#</div>
                    </th>
                  )}
                  {cols.map((col) => {
                    return (
                      <th
                        key={col.id}
                        scope="col"
                        className="px-4 py-3.5 text-left text-sm font-normal rtl:text-right">
                        <div className={classNames(col.id === "actions" && "flex justify-end")}>
                          {col.children}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-subtle divide-y align-top">
                {isLoading ? (
                  <TableBodySkeleton perPage={perPage} colNumbers={cols.length} />
                ) : (
                  <>
                    {rows.map((row, rowIndex) => (
                      <tr
                        key={row.id}
                        onClick={row.onClick}
                        className={classNames(
                          "group",
                          Boolean(row.onClick) && "hover:bg-subtle cursor-pointer"
                        )}>
                        {rowNumber && (
                          <td className={classNames("py-2 text-sm")}>
                            <div className={classNames("flex h-full items-center justify-end")}>
                              {getRowNumber(rowIndex)}
                            </div>
                          </td>
                        )}
                        {row.cells.map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames(
                                "h-full px-4 py-2 text-sm",
                                cell.id === "actions" && "flex justify-end"
                              )}>
                              <div
                                className={classNames(
                                  "flex h-full w-full items-center",
                                  cell.id === "actions" && "flex justify-end"
                                )}>
                                {cell.children}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {paginationProps && <PaginationComponent {...paginationProps} />}
    </div>
  );
};

const PaginationButton = ({
  className,
  children,
  ...rest
}: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>) => {
  return (
    <button
      {...rest}
      className={classNames(
        "text-emphasis min-w-10 border-default bg-default disabled:border-subtle disabled:text-muted disabled:hover:text-muted disabled:hover:border-subtle disabled:hover:bg-default h-9 whitespace-nowrap border p-1 text-center text-sm transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-opacity-30 disabled:hover:bg-opacity-30",
        className
      )}>
      {children}
    </button>
  );
};

export type PaginationComponentProps = {
  setPerPage: (perPage: number) => void;
  setCurrentPage: (page: number) => void;
  pagination: Pagination;
};

const PaginationComponent = ({
  pagination: { perPage, totalRecords, currentPage, totalPages },
  setCurrentPage,
  setPerPage,
}: PaginationComponentProps) => {
  if (!totalRecords) return null;
  const series = getSeries(totalPages, currentPage);
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-between">
      <div className="my-2 flex items-center gap-2 md:mb-0">
        <p className="text-sm">
          <span className="">showing </span>
          <strong>{perPage * (currentPage - 1) + 1}</strong>
          <span> - </span>
          <strong>{currentPage * perPage}</strong>
          <span> of </span>
          <strong>{totalRecords}</strong>
        </p>
        <Select
          placeholder="Per page"
          className="shrink-0"
          options={perPageOptions}
          value={perPageOptions.find((option) => option.value === perPage)}
          onChange={(option) => {
            if (!option) return;
            setPerPage(option.value);
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        {series.map((item, index) => {
          const isCurrent = currentPage === Number(item);
          const isClickable = typeof item === "number" && !isCurrent;
          return (
            <PaginationButton
              key={index}
              onClick={() => {
                if (typeof item === "number") {
                  setCurrentPage(item);
                }
              }}
              className={classNames(
                index === 0 && "rounded-l-md",
                index === series.length - 1 && "rounded-r-md",
                isClickable ? "hover:underline" : "cursor-default",
                isCurrent && "bg-emphasis"
              )}>
              {item}
            </PaginationButton>
          );
        })}
      </div>
    </div>
  );
};

const TableBodySkeleton = ({ perPage, colNumbers }: { perPage: number; colNumbers: number }) => {
  const itemClassNames =
    "animate-pulse rounded-sm text-sm font-bold font-normal text-transparent opacity-90 bg-muted";
  return (
    <>
      {[...Array(perPage)].map((_, index) => (
        <tr key={index}>
          {Array.from({ length: colNumbers }).map((_, index) => (
            <td key={index} className="px-4 py-2 text-sm">
              <div>
                <p className={itemClassNames}>loading please wait</p>
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

export default Table;
